import type { VoiceCommandResult } from './voiceCommandClient';

export type ConfirmFieldEmphasis = 'primary' | 'secondary' | 'warning';

export interface VoiceConfirmCardField {
  label: string;
  value: string;
  emphasis?: ConfirmFieldEmphasis;
}

export interface VoiceConfirmCardContent {
  title: string;
  fields: VoiceConfirmCardField[];
}

const INTENT_TITLES: Record<string, string> = {
  create_contact: 'Add contact',
  create_customer: 'Add customer',
  create_supplier: 'Add supplier',
  update_contact: 'Update contact',
  delete_contact: 'Remove contact',
  record_expense: 'Record expense',
  create_expense: 'Record expense',
  create_sales_invoice: 'Create invoice',
  create_invoice: 'Create invoice',
  create_purchase_invoice: 'Record bill',
  create_bill: 'Record bill',
  create_quote: 'Create quotation',
  record_payment: 'Record payment',
  record_customer_payment: 'Record payment received',
  confirm_cheque_deposit: 'Record cheque deposit',
  record_supplier_payment: 'Record payment sent',
  void_invoice: 'Void invoice',
  delete_invoice: 'Delete invoice',
  convert_quote_to_invoice: 'Convert to invoice',
};

const HIDDEN_KEYS = new Set([
  'business_id',
  'is_active',
  'entities',
  'summary',
  'command_id',
  'intent',
  'type',
  'action',
  'resolved_ids',
  'entity_versions',
  'entity_versions_snapshot',
  'confidence',
  'contact_id',
  'customer_id',
  'supplier_id',
  'invoice_id',
  'expense_id',
  'quote_id',
  'id',
  'create_new_contact',
  'delayed_until_execution',
]);

const LABEL_OVERRIDES: Record<string, string> = {
  customer_name: 'Customer',
  supplier_name: 'Supplier',
  contact_name: 'Contact',
  company_name: 'Name',
  new_name: 'New name',
  amount: 'Amount',
  total: 'Total',
  tax: 'Tax',
  tax_amount: 'Tax',
  currency: 'Currency',
  description: 'Description',
  notes: 'Notes',
  payment_date: 'Date',
  expense_date: 'Date',
  invoice_date: 'Date',
  transaction_date: 'Date',
  date: 'Date',
  expires_at: 'Confirm by',
  expires: 'Confirm by',
  item_description: 'Item',
};

function humanizeKey(key: string): string {
  return LABEL_OVERRIDES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMoney(amount: unknown, currency?: string | null): string {
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(num)) return String(amount ?? '');
  const cur = currency && String(currency).trim() ? String(currency).trim() : '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: cur ? 'currency' : 'decimal',
      currency: cur || 'AED',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return cur ? `${cur} ${num.toFixed(2)}` : num.toFixed(2);
  }
}

function formatDisplayDate(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const parsed = new Date(raw.includes('T') ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function entityValue(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'object' && raw !== null && 'value' in (raw as object)) {
    const v = (raw as { value?: unknown }).value;
    return v == null ? null : String(v);
  }
  return String(raw);
}

function resolveTitle(intent: string | undefined, message?: string): string {
  if (intent && INTENT_TITLES[intent]) return INTENT_TITLES[intent];
  const msg = (message || '').toLowerCase();
  if (msg.includes('invoice')) return 'Confirm invoice';
  if (msg.includes('expense')) return 'Confirm expense';
  if (msg.includes('payment')) return 'Confirm payment';
  if (msg.includes('contact') || msg.includes('customer') || msg.includes('supplier')) {
    return 'Confirm contact';
  }
  if (msg.includes('quote') || msg.includes('quotation')) return 'Confirm quotation';
  return 'Please confirm';
}

type CanonicalSummary = {
  intent?: string;
  type?: string;
  entity?: { id?: number | string; name?: string | null };
  new_name?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  total?: number | string | null;
  tax?: number | string | null;
  date?: string | null;
  accounts?: Array<{ role?: string; name?: string | null }> | null;
  invoice_allocations?: Array<{
    invoice_number?: string;
    invoice_id?: number | string;
    amount?: number | string;
  }> | null;
  destructive_effect?: string | null;
  pending_contact_create?: { create?: boolean; name?: string | null } | null;
  expires_at?: string | null;
};

function buildFromCanonicalSummary(summary: CanonicalSummary): VoiceConfirmCardField[] {
  const fields: VoiceConfirmCardField[] = [];
  const intent = summary.intent || summary.type;

  const contactName = summary.entity?.name?.trim();
  if (contactName && !/^(this|it)$/i.test(contactName)) {
    const label =
      intent?.includes('supplier') || intent?.includes('purchase') ? 'Supplier' : 'Customer';
    fields.push({ label: label === 'Customer' && intent === 'create_contact' ? 'Contact' : label, value: contactName, emphasis: 'primary' });
  }

  if (summary.new_name) {
    fields.push({ label: 'New name', value: String(summary.new_name), emphasis: 'primary' });
  }

  if (summary.amount != null) {
    fields.push({
      label: 'Amount',
      value: formatMoney(summary.amount, summary.currency),
      emphasis: 'primary',
    });
  } else if (summary.total != null) {
    fields.push({
      label: 'Total',
      value: formatMoney(summary.total, summary.currency),
      emphasis: 'primary',
    });
  }

  if (Array.isArray(summary.invoice_allocations) && summary.invoice_allocations.length) {
    for (const row of summary.invoice_allocations.slice(0, 4)) {
      const label = row.invoice_number ? `Invoice ${row.invoice_number}` : 'Invoice';
      fields.push({
        label,
        value: formatMoney(row.amount, summary.currency),
        emphasis: 'primary',
      });
    }
  }

  if (Array.isArray(summary.accounts)) {
    const roleLabel: Record<string, string> = {
      from: 'From account',
      to: 'To account',
      debit: 'Debit',
      credit: 'Credit',
      deposit: 'Bank account',
      account: 'Account',
    };
    for (const acct of summary.accounts) {
      if (!acct?.name) continue;
      fields.push({
        label: roleLabel[acct.role || 'account'] || 'Account',
        value: acct.name,
        emphasis: 'primary',
      });
    }
  }

  if (summary.pending_contact_create?.create) {
    fields.push({
      label: 'Also adding',
      value: summary.pending_contact_create.name || 'New contact',
      emphasis: 'secondary',
    });
  }

  if (summary.destructive_effect) {
    fields.push({
      label: 'Heads up',
      value: summary.destructive_effect,
      emphasis: 'warning',
    });
  }

  if (summary.tax != null && summary.total != null && summary.tax !== summary.total) {
    fields.push({
      label: 'Tax',
      value: formatMoney(summary.tax, summary.currency),
      emphasis: 'secondary',
    });
  }

  if (summary.date) {
    fields.push({
      label: 'Date',
      value: formatDisplayDate(summary.date),
      emphasis: 'secondary',
    });
  }

  if (summary.expires_at) {
    fields.push({
      label: 'Confirm by',
      value: formatDisplayDate(summary.expires_at),
      emphasis: 'secondary',
    });
  }

  return fields;
}

function buildFromFlatRecord(data: Record<string, unknown>): VoiceConfirmCardField[] {
  const fields: VoiceConfirmCardField[] = [];
  const currency =
    entityValue(data.currency) || entityValue(data.currency_code) || null;

  const primaryOrder = [
    'customer_name',
    'supplier_name',
    'contact_name',
    'company_name',
    'name',
    'new_name',
    'description',
    'item_description',
    'amount',
    'total',
  ];

  for (const key of primaryOrder) {
    if (!(key in data)) continue;
    const value = entityValue(data[key]);
    if (!value) continue;
    const isMoney = key === 'amount' || key === 'total';
    fields.push({
      label: humanizeKey(key),
      value: isMoney ? formatMoney(value, currency) : value,
      emphasis: 'primary',
    });
  }

  const secondaryOrder = ['payment_date', 'expense_date', 'invoice_date', 'transaction_date', 'date', 'expires_at', 'expires', 'tax', 'tax_amount', 'notes'];

  for (const key of secondaryOrder) {
    if (!(key in data) || primaryOrder.includes(key)) continue;
    const value = entityValue(data[key]);
    if (!value) continue;
    const isDate = key.includes('date') || key.startsWith('expires');
    fields.push({
      label: humanizeKey(key),
      value: isDate ? formatDisplayDate(value) : value,
      emphasis: 'secondary',
    });
  }

  for (const [key, raw] of Object.entries(data)) {
    if (HIDDEN_KEYS.has(key) || primaryOrder.includes(key) || secondaryOrder.includes(key)) continue;
    const value = entityValue(raw);
    if (!value || value.length > 120) continue;
    if (/^\d+$/.test(value) && key.endsWith('_id')) continue;
    fields.push({ label: humanizeKey(key), value, emphasis: 'secondary' });
  }

  return fields.slice(0, 8);
}

function messageFallbackField(message?: string): VoiceConfirmCardField[] {
  const firstLine = (message || '').split('\n').map((l) => l.trim()).find(Boolean);
  if (!firstLine) {
    return [{ label: 'Action', value: 'Confirm pending action', emphasis: 'primary' }];
  }
  const cleaned = firstLine
    .replace(/^please confirm:?\s*/i, '')
    .replace(/\s*—\s*confirm\?$/i, '')
    .trim();
  return [{ label: 'Summary', value: cleaned || firstLine, emphasis: 'primary' }];
}

export function buildVoiceConfirmCardContent(result: VoiceCommandResult): VoiceConfirmCardContent {
  const summary = (result.pending_action?.summary || {}) as CanonicalSummary;
  const intent =
    summary.intent
    || summary.type
    || (typeof result.pending_action?.type === 'string' ? result.pending_action.type : undefined)
    || result.data?.intent;

  let fields = buildFromCanonicalSummary(summary);

  if (!fields.length) {
    const data =
      (result.pendingAction?.data as Record<string, unknown> | undefined)
      || (result.pending_action?.data as Record<string, unknown> | undefined);
    if (data && typeof data === 'object') {
      fields = buildFromFlatRecord(data);
    }
  }

  if (!fields.length) {
    fields = messageFallbackField(result.message);
  }

  return {
    title: resolveTitle(typeof intent === 'string' ? intent : undefined, result.message),
    fields,
  };
}
